import { create } from 'zustand';
import { CareerAnalysisData, safeParseCareerAnalysis, formatAnalysisForStorage, validateCareerAnalysis } from '../types/analysis';
import { supabaseClient, fetchFromSupabase, saveToSupabase } from '../config/supabase';
import toast from 'react-hot-toast';

interface AnalysisState {
  analysis: CareerAnalysisData | null;
  analysisLoading: boolean;
  analysisError: string | null;
  setAnalysis: (analysisData: CareerAnalysisData | string | null) => void;
  setAnalysisLoading: (loading: boolean) => void;
  setAnalysisError: (error: string | null) => void;
  clearAnalysis: () => void;
  saveAnalysisToDatabase: (userId: string) => Promise<boolean>;
  fetchAnalysisFromDatabase: (userId: string) => Promise<CareerAnalysisData | null>;
}

export const useAnalysisStore = create<AnalysisState>((set, get) => ({
  analysis: null,
  analysisLoading: false,
  analysisError: null,
  
  setAnalysis: (analysisData) => {
    if (typeof analysisData === 'string') {
      const parsedAnalysis = safeParseCareerAnalysis(analysisData);
      set({ analysis: parsedAnalysis });
      
      // Zapisz do localStorage
      if (parsedAnalysis) {
        try {
          const userId = supabaseClient.auth.getUser().then(({ data }) => {
            if (data?.user) {
              localStorage.setItem(`analysis_${data.user.id}`, formatAnalysisForStorage(parsedAnalysis));
            }
          });
        } catch (error) {
          console.error('Błąd podczas zapisywania analizy do localStorage:', error);
        }
      }
    } else {
      set({ analysis: analysisData });
      
      // Zapisz do localStorage jeśli istnieje
      if (analysisData) {
        try {
          const userId = supabaseClient.auth.getUser().then(({ data }) => {
            if (data?.user) {
              localStorage.setItem(`analysis_${data.user.id}`, formatAnalysisForStorage(analysisData));
            }
          });
        } catch (error) {
          console.error('Błąd podczas zapisywania analizy do localStorage:', error);
        }
      }
    }
  },
  
  setAnalysisLoading: (loading) => set({ analysisLoading: loading }),
  
  setAnalysisError: (error) => set({ analysisError: error }),
  
  clearAnalysis: () => set({ analysis: null, analysisError: null }),
  
  saveAnalysisToDatabase: async (userId: string) => {
    const { analysis } = get();
    if (!analysis) {
      console.warn('[AnalysisStore] Brak analizy w stanie do zapisania.');
      return false;
    }
    if (!validateCareerAnalysis(analysis)) {
      console.error('[AnalysisStore] Analiza nie przeszła walidacji i nie zostanie zapisana.');
      return false;
    }

    try {
      const formattedAnalysis = formatAnalysisForStorage(analysis);
      // Zapisz do localStorage jako backup
      localStorage.setItem(`analysis_${userId}`, formattedAnalysis);
      console.log('[AnalysisStore] Zapisano analizę do localStorage.');

      // Użyj saveToSupabase z opcją upsert
      const { data: savedData, error: dbError } = await saveToSupabase({
        tableName: 'career_analyses',
        data: {
          user_id: userId,
          analysis: formattedAnalysis,
          // updated_at będzie zarządzane przez Supabase (jeśli kolumna istnieje)
        },
        options: {
          updateExisting: true, // Użyj UPSERT
        }
      });

      if (dbError) {
        console.error('[AnalysisStore] Błąd podczas zapisywania analizy do Supabase:', dbError);
        // Nie zwracaj false, jeśli localStorage się powiódł? Zależy od logiki biznesowej.
        // Na razie zwracamy false, jeśli zapis do DB się nie powiedzie.
        return false;
      }

      console.log('[AnalysisStore] Analiza zapisana/zaktualizowana w Supabase.');
      return true;

    } catch (error) {
      console.error('[AnalysisStore] Nieoczekiwany błąd podczas zapisywania analizy:', error);
      return false;
    }
  },
  
  fetchAnalysisFromDatabase: async (userId: string) => {
    set({ analysisLoading: true, analysisError: null }); // Resetuj błąd przy pobieraniu

    try {
      // Użyj fetchFromSupabase z opcją single: true
      const { data: dbAnalysisData, error: dbError } = await fetchFromSupabase<{ analysis: string }>({
        tableName: 'career_analyses',
        columns: 'analysis', // Pobierz tylko kolumnę z analizą
        filters: { user_id: userId },
        single: true, // Oczekujemy jednego lub zero wyników
        useCache: false // Lepiej nie cachować tego przy pierwszym ładowaniu dashboardu
      });

      if (dbError) {
        console.error('[AnalysisStore] Błąd podczas pobierania analizy z Supabase:', dbError);
        // Nie ustawiaj błędu od razu, spróbuj localStorage
      }

      // Sprawdź, czy dane z DB istnieją i są poprawne
      if (dbAnalysisData?.analysis) {
        console.log('[AnalysisStore] Analiza pobrana z Supabase.');
        const parsedAnalysis = safeParseCareerAnalysis(dbAnalysisData.analysis);
        if (parsedAnalysis) {
          set({ analysis: parsedAnalysis, analysisLoading: false });
          // Zapisz do localStorage jako backup
          localStorage.setItem(`analysis_${userId}`, formatAnalysisForStorage(parsedAnalysis));
          return parsedAnalysis;
        } else {
          console.warn('[AnalysisStore] Nie udało się sparsować analizy pobranej z Supabase.');
          // Nie ustawiaj błędu, spróbuj localStorage
        }
      } else {
         console.log('[AnalysisStore] Brak analizy w Supabase dla tego użytkownika.');
      }

      // Jeśli nie ma danych w DB lub były niepoprawne, spróbuj localStorage
      console.log('[AnalysisStore] Próba pobrania analizy z localStorage...');
      const localAnalysisString = localStorage.getItem(`analysis_${userId}`);
      if (localAnalysisString) {
        const parsedLocalAnalysis = safeParseCareerAnalysis(localAnalysisString);
        if (parsedLocalAnalysis) {
          console.log('[AnalysisStore] Analiza pobrana z localStorage.');
          set({ analysis: parsedLocalAnalysis, analysisLoading: false });
          // Opcjonalnie: spróbuj zapisać do DB w tle, jeśli jej tam nie było
          if (!dbAnalysisData?.analysis) {
             console.log('[AnalysisStore] Próba zapisu analizy z localStorage do Supabase w tle...');
             get().saveAnalysisToDatabase(userId).catch(err => console.warn('[AnalysisStore] Zapis w tle nie powiódł się:', err));
          }
          return parsedLocalAnalysis;
        } else {
          console.warn('[AnalysisStore] Nie udało się sparsować analizy z localStorage.');
          localStorage.removeItem(`analysis_${userId}`); // Usuń niepoprawne dane
        }
      } else {
         console.log('[AnalysisStore] Brak analizy w localStorage.');
      }

      // Jeśli nigdzie nie znaleziono analizy
      set({ analysis: null, analysisLoading: false, analysisError: dbError ? 'Błąd pobierania danych z serwera.' : null }); // Ustaw błąd tylko jeśli był problem z DB
      return null;

    } catch (error) {
      console.error('[AnalysisStore] Nieoczekiwany błąd podczas pobierania analizy:', error);
      set({ analysisError: 'Wystąpił nieoczekiwany błąd', analysisLoading: false });
      return null;
    }
  }
}));
